import { MedplumClient } from "@medplum/core";
import { ValueSetExpansionContains } from "@medplum/fhirtypes";

import { ProceduresValueSets } from "@/fhir/enums/procedures";
import { RETIRED_PROCEDURE_CODES } from "@/shared/procedure-code-effective-dates";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { HCPCS_CODE_SYSTEM } from "../constants/combined-procedure-codes";
import { CUSTOM_PROCEDURE_CODE_SYSTEM } from "../constants/custom-codes";
import { isBillableHostedCoding } from "../utils/billable-hosted-coding";

/**
 * Extra results requested from the billable procedure codes ValueSet, so a full page remains after
 * HCPCS modifiers and retired or switched-off codes are dropped.
 */
const HOSTED_SEARCH_EXTRA_RESULTS = 10;

/**
 * Searches the practice's procedure codes: the fee and custom codes stored in its combined
 * ValueSet, plus the billable CPT and HCPCS Level II codes searched live. An unfiltered expansion
 * of the hosted codes returns only its first page, so they are searched with the user's text as
 * `filter`.
 *
 * - A hosted code that is a fee code shows as the practice's fee entry, so its fee name is kept.
 * - A hosted code the practice switched off in its fee schedule is not offered.
 * - A custom code and a hosted code with the same value both appear.
 * - A code listed under both CPT and HCPCS shows once, as the HCPCS code.
 * - HCPCS modifiers, other code systems, and retired or inactive codes from the hosted response
 *   are dropped.
 *
 * @param medplum The Medplum client.
 * @param combinedValueSetUrl The practice's combined procedure codes ValueSet URL.
 * @param params `filter` is the search text; `count` caps the number of results.
 * @returns Practice matches first, then hosted matches, unique by system and code.
 * @throws When the combined ValueSet cannot be read. A failed hosted search is reported and returns
 * the practice matches only.
 */
export async function searchCombinedProcedureCodes(
  medplum: MedplumClient,
  combinedValueSetUrl: string,
  { filter, count }: { filter: string; count: number },
): Promise<ValueSetExpansionContains[]> {
  const [storedCodings, hostedCodings] = await Promise.all([
    readStoredCodings(medplum, combinedValueSetUrl),
    searchHostedProcedureCodes(medplum, filter, count + HOSTED_SEARCH_EXTRA_RESULTS),
  ]);

  const switchedOffCodes = new Set<string>();
  const activeFeeCodingsByCode = new Map<string, ValueSetExpansionContains>();
  for (const coding of storedCodings) {
    if (!coding.code || coding.system?.startsWith(CUSTOM_PROCEDURE_CODE_SYSTEM)) continue;
    if (coding.inactive) switchedOffCodes.add(coding.code);
    else if (!activeFeeCodingsByCode.has(coding.code))
      activeFeeCodingsByCode.set(coding.code, coding);
  }

  const searchText = filter.toLowerCase();
  const practiceMatches = storedCodings.filter(
    (coding) =>
      !coding.inactive &&
      (coding.code?.toLowerCase().includes(searchText) ||
        coding.display?.toLowerCase().includes(searchText)),
  );

  const hostedCodingsByCode = hostedCodings.reduce((codingsByCode, coding) => {
    if (!isBillableHostedCoding(coding)) return codingsByCode;
    const code = coding.code;
    const isOffered =
      !coding.inactive && !RETIRED_PROCEDURE_CODES.has(code) && !switchedOffCodes.has(code);
    if (isOffered && (!codingsByCode.has(code) || coding.system === HCPCS_CODE_SYSTEM))
      codingsByCode.set(code, coding);
    return codingsByCode;
  }, new Map<string, ValueSetExpansionContains>());
  const hostedMatches = Array.from(
    hostedCodingsByCode,
    ([code, coding]) => activeFeeCodingsByCode.get(code) ?? coding,
  );

  const resultsByKey = new Map<string, ValueSetExpansionContains>();
  for (const coding of [...practiceMatches, ...hostedMatches]) {
    const key = `${coding.system}|${coding.code}`;
    if (!resultsByKey.has(key)) resultsByKey.set(key, coding);
  }

  return Array.from(resultsByKey.values()).slice(0, count);
}

async function readStoredCodings(
  medplum: MedplumClient,
  combinedValueSetUrl: string,
): Promise<ValueSetExpansionContains[]> {
  const valueSet = await medplum.searchOne("ValueSet", { url: combinedValueSetUrl });
  return valueSet?.expansion?.contains ?? [];
}

async function searchHostedProcedureCodes(
  medplum: MedplumClient,
  filter: string,
  count: number,
): Promise<ValueSetExpansionContains[]> {
  try {
    const valueSet = await medplum.valueSetExpand({
      url: ProceduresValueSets.BillableProcedureCodes,
      filter,
      count,
    });
    return valueSet.expansion?.contains ?? [];
  } catch (error) {
    // The search text is not reported: it can be anything the user typed.
    captureExceptionWithoutPhi(
      "Live procedure code search failed; showing fee and custom codes only",
      error,
      {
        feature: "combined-procedure-codes",
        operation: "search-billable-procedure-value-set",
        valueSetUrl: ProceduresValueSets.BillableProcedureCodes,
      },
      "warning",
    );
    return [];
  }
}
